// SavedCards.tsx
import React from "react";
import { motion } from "framer-motion";
import { CreditCard, Trash2,  Plus, ShieldCheck, Copy, BadgeCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CreditCardType } from "../../types";


interface SavedCardsProps {
    cards: CreditCardType[];
    loading?: boolean;
    onDelete?: (id: number) => void;
    onAddNew?: () => void;
    logos?: Partial<Record<string, string>>;
}

// -----------------------------
// Helpers
// -----------------------------
const isExpired = (m: number, y: number) => {
    const now = new Date();
    const exp = new Date(y, m - 1, 1);
    exp.setMonth(exp.getMonth() + 1);
    exp.setDate(0);
    // compare end-of-month to today's date (midnight)
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return exp < today;
};

const getLast4 = (num: string) => {
    if (!num) return "0000";
    return num.replace(/\s+/g, "").slice(-4);
};

const parseExpiry = (expiry: string) => {
    if (!expiry) return { expMonth: 0, expYear: 0 };
    const parts = expiry.split("/").map((p) => p.trim());
    const month = parseInt(parts[0] ?? "0", 10) || 0;
    let year = 0;
    if (parts[1]) {
        year = parts[1].length === 2 ? parseInt(`20${parts[1]}`, 10) : parseInt(parts[1], 10);
    }
    return { expMonth: month, expYear: year };
};

const mask = (last4: string) => `•••• •••• •••• ${last4}`;

// Small shimmering overlay for premium feel
const Shine: React.FC = () => (
    <div className="pointer-events-none absolute inset-0 [mask-image:linear-gradient(90deg,transparent,black,transparent)]">
        <div className="absolute -inset-x-1/2 -inset-y-1 bg-white/20 animate-[shimmer_3s_linear_infinite] [transform:skewX(-20deg)]" />
        <style>{`
      @keyframes shimmer { 0% { transform: translateX(-60%) skewX(-20deg) } 100% { transform: translateX(60%) skewX(-20deg) } }
    `}</style>
    </div>
);

// A single card UI
const CardTile: React.FC<{
    card: CreditCardType;
    onDelete?: (id: number) => void;
    logo?: string;
}> = ({ card, onDelete, logo }) => {
    const { t } = useTranslation();
    const last4 = getLast4(card.cardNumber);
    const { expMonth, expYear } = parseExpiry(card.expiryDate);
    const expired = isExpired(expMonth, expYear);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -3 }}
            className="relative"
        >
            <div className="relative rounded-2xl p-4 sm:p-5 aspect-[16/10] shadow-lg bg-gradient-to-br from-slate-400 to-slate-600 overflow-hidden">
                
                <div className="absolute inset-0 opacity-30 mix-blend-overlay bg-[radial-gradient(#ffffff33_1px,transparent_1px)] [background-size:12px_12px]" />
                <Shine />

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-white/90">
                        <div className="w-10 h-7 flex items-center justify-center">
                            {logo ? <img src={logo} alt="logo" className="max-w-full max-h-full object-contain" /> : <CreditCard className="w-6 h-6 text-white" />}
                        </div>
                    </div>
                    <ShieldCheck className="w-5 h-5 text-white/80" />
                </div>

                {/* number */}
                <div className="mt-6 text-white tracking-widest text-lg sm:text-xl md:text-2xl font-mono drop-shadow-sm select-all">
                    {mask(last4)}
                </div>

                {/* footer */}
                <div className="mt-6 flex items-end justify-between text-white/90">
                    <div className="text-xs sm:text-sm">
                        <div className="opacity-80">{t("labels.cardHolder")}</div>
                        <div className="font-semibold leading-tight">{card.cardHolderName}</div>
                    </div>
                    <div className="text-right text-xs sm:text-sm">
                        <div className="opacity-80">{t("labels.expires")}</div>
                        <div className="font-semibold leading-tight tabular-nums">
                            {String(expMonth).padStart(2, "0")}/{String(expYear).slice(-2)}
                        </div>
                        {expired && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-black/30">
                                <BadgeCheck className="w-3 h-3 rotate-45" /> {t("labels.expired")}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* actions bar */}
            <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigator.clipboard?.writeText(card.cardNumber)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-slate-200 hover:bg-slate-300 text-slate-800"
                    >
                        <Copy className="w-3.5 h-3.5" /> {t("buttons.copyNumber")}
                    </button> 
                    <button
                        onClick={() => onDelete?.(card.id)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-rose-600 text-white hover:bg-rose-700"
                    >
                        <Trash2 className="w-3.5 h-3.5" /> {t("buttons.delete")}
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

// Skeleton while loading
const CardSkeleton: React.FC = () => (
    <div className="animate-pulse">
        <div className="rounded-2xl h-40 sm:h-44 bg-slate-200" />
        <div className="h-6" />
        <div className="flex justify-between">
            <div className="w-24 h-6 bg-slate-200 rounded" />
            <div className="w-48 h-6 bg-slate-200 rounded" />
        </div>
    </div>
);

// Empty state
const EmptyState: React.FC<{ onAdd?: () => void }> = ({ onAdd }) => {
    const { t } = useTranslation();
    return (
        <div className="col-span-full flex flex-col items-center justify-center text-center rounded-2xl border border-dashed p-10 bg-slate-50">
            <CreditCard className="w-10 h-10 text-slate-400" />
            <h3 className="mt-2 text-slate-700 font-semibold">{t("empty.title")}</h3>
            <p className="mt-1 text-slate-500 text-sm">{t("empty.subtitle")}</p>
            <button onClick={onAdd} className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                <Plus className="w-4 h-4" /> {t("buttons.addCard")}
            </button>
        </div>
    );
};

const SavedCards: React.FC<SavedCardsProps> = ({ cards, loading,  onDelete,  onAddNew, logos }) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir ? i18n.dir() : i18n.language === "ar" ? "rtl" : "ltr";

    return (
        <div className="w-full" dir={dir}>
            <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-bold">{t("card-title")}</h2>
                <button onClick={onAddNew} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-white hover:bg-[#00610d]">
                    <Plus className="w-4 h-4" /> {t("buttons.addCard")}
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {loading && Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)}

                {!loading && cards.length === 0 && <EmptyState onAdd={onAddNew} />}

                {!loading && cards.map((c) => (
                    <CardTile
                        key={c.id}
                        card={c}
                        onDelete={onDelete}
                        logo={logos?.[String(c.userId)]}
                    />
                ))}
            </div>

            <p className="mt-6 text-xs text-slate-500 flex items-center gap-1">
                <ShieldCheck className="w-4 h-4" />
                {t("notes.security")}
            </p>
        </div>
    );
};

export default SavedCards;
